import { LuPhoneCall, LuClock3, LuEye } from "react-icons/lu";
import { MdOutlineEmail, MdOutlineLocationOn } from "react-icons/md";
import { BiClinic } from "react-icons/bi";
import { useDoctorQuery } from "../../hooks/useDoctorQuery";
import { FaRegHospital } from "react-icons/fa6";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useDoctorContext } from "../../context/DoctorContext";
import NeedAuthModal from "../Modals/NeedAuthModal";
import GuestBookingModal from "../Modals/GuestInformationModal";
import { useRef, useState } from "react";
import AnimatedButton from "../Buttons/AnimatedButton";
import { useLanguage } from "../../context/LanguageContext";
import { Calendar1Icon, Copy, MapIcon } from "lucide-react";
import { Button, message } from "antd";
import LoginModal from "../Modals/LoginModal";
import { slug } from "../../utils/store";

const formatTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return "";
  const [hour, minute] = timeStr.split(":");
  const hourNum = parseInt(hour, 10);
  if (isNaN(hourNum)) return timeStr;
  const ampm = hourNum >= 12 ? "PM" : "AM";
  const formattedHour = hourNum % 12 || 12;
  return `${formattedHour}:${minute} ${ampm}`;
};

const getDayName = (dayOfWeek, t) => {
  const days = [
    t("common.days.sunday"),
    t("common.days.monday"),
    t("common.days.tuesday"),
    t("common.days.wednesday"),
    t("common.days.thursday"),
    t("common.days.friday"),
    t("common.days.saturday"),
  ];
  return days[dayOfWeek] || t("common.days.unknown");
};
 export const ClinicDetailsModal = ({ clinic, isOpen, onClose,t ,handleProtectedNavigation,hide=false}) => {
    if (!isOpen) return null;

    const allWorkingDays = [
      ...(clinic?.genralDays ?? []),
      ...(clinic?.doctors?.flatMap((doctor) => doctor.workingDays ?? []) ?? []),
    ]
      .filter((day) => day.isWorking)
      .reduce((unique, day) => {
        if (!unique.some((d) => d.dayOfWeek === day.dayOfWeek)) {
          unique.push(day);
        }
        return unique;
      }, [])
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
   return (
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4"
      >
        <motion.div
          className="bg-white rounded-2xl p-4 sm:p-6 max-w-xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-base sm:text-2xl font-bold text-gray-500 flex items-center gap-2">
              <BiClinic className="text-lg sm:text-2xl text-gray-500 shrink-0" />
              <span className="line-clamp-1">{clinic.clinicName}</span>
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none ml-2 shrink-0"
            >
              ×
            </button>
          </div>

          <div className="space-y-4 sm:space-y-6 text-xs sm:text-sm text-textPrimary">
            {clinic.hasLocation ? (
              <div className="space-y-2 sm:space-y-4">
                {/* Address */}
                <div className="flex items-start gap-2 sm:gap-3 bg-primary/10 p-2 sm:p-3 border rounded-xl">
                  <MdOutlineLocationOn className="text-lg sm:text-2xl mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="font-semibold mb-1 sm:mb-2 text-sm sm:text-lg">
                      {t("clinics.details.address")}
                    </p>
                    <p className="text-gray-700 text-xs sm:text-sm">{clinic.detailedAddress}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <div className="flex items-start gap-2 sm:gap-3 bg-primary/10 p-2 sm:p-3 border rounded-xl">
                    <LuPhoneCall className="text-base sm:text-2xl mt-0.5 text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold mb-1 sm:mb-2 text-xs sm:text-sm">
                        {t("clinics.details.phone")}
                      </p>
                      <p className="text-gray-700 text-xs truncate">
                        {clinic.contactPhone || t("common.notAvailable")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3 bg-primary/10 p-2 sm:p-3 border rounded-xl">
                    <MdOutlineEmail className="text-base sm:text-2xl mt-0.5 text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold mb-1 sm:mb-2 text-xs sm:text-sm">
                        {t("clinics.details.email")}
                      </p>
                      <p className="text-gray-700 text-xs truncate">
                        {clinic.contactEmail || t("common.notAvailable")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Working Hours */}
            <div className="flex items-start gap-2 sm:gap-3">
              <Calendar1Icon className="text-lg sm:text-2xl mt-0.5 text-primary shrink-0" />
              <div className="w-full">
                <p className="font-semibold mb-2 sm:mb-3 text-sm sm:text-lg">
                  {t("clinics.details.workingHours")}
                </p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {allWorkingDays.length > 0 ? (
                    allWorkingDays
                      .sort((a, b) => ((a.dayOfWeek + 1) % 7) - ((b.dayOfWeek + 1) % 7))
                      .map((day) => (
                        <span
                          key={day.dayOfWeek}
                          className="border border-primary text-primary px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium"
                        >
                          {getDayName(day.dayOfWeek, t)}
                        </span>
                      ))
                  ) : (
                    <p className="text-gray-500 text-xs sm:text-sm">{t("clinics.soon")}..</p>
                  )}
                </div>
              </div>
            </div>

            {/* Doctors */}
            {clinic.doctors && clinic.doctors.length > 0 && (
              <div className="flex items-start gap-2 sm:gap-3">
                <FaRegHospital className="text-lg sm:text-2xl mt-0.5 text-primary shrink-0" />
                <div className="w-full">
                  <p className="font-semibold mb-2 sm:mb-3 text-sm sm:text-lg">
                    {t("clinics.details.availableDoctors")}
                  </p>
                  <div className="space-y-2 sm:space-y-3">
                    {clinic.doctors.map((doctor) => (
                      <div key={doctor.doctorId} className="bg-gray-50 p-2 sm:p-3 rounded-lg">
                        <p className="font-medium text-primary text-sm sm:text-base">
                          {doctor.doctorName}
                        </p>
                        {doctor.specialization && (
                          <p className="text-gray-600 text-xs sm:text-sm mt-0.5 sm:mt-1">
                            {t("clinics.details.specialization")}: {doctor.specialization}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="mt-5 sm:mt-8 flex gap-2 sm:gap-3">
            {!hide && (
              <button
                className="flex-1 bg-gold text-white font-semibold py-2.5 sm:py-3 px-3 sm:px-6 text-xs sm:text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-300 shadow-md hover:shadow-lg"
                onClick={() => {
                  onClose();
                  handleProtectedNavigation(`/book-appointment?ClinicId=${clinic.clinicId}&showOptions=false`);
                }}
                disabled={allWorkingDays.length <= 0}
              >
                {t("clinics.bookAppointment")}
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t("common.close")}
            </button>
          </div>
        </motion.div>
      </div>
    );

  };
const ClinicsSection = () => {
  const { t } = useLanguage();
  const { data: doctor } = useDoctorQuery();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [expandedClinic, setExpandedClinic] = useState(null);
  const [loginModal, setLoginModal] = useState(false);
  const [registerModal, setRegisterModal] = useState(false);
  const [showAll, setShowAll] = useState(false); // 👈 new
  const sectionRef = useRef(null);

  const isAuthorized =
    !!localStorage.getItem(`userTkn-${slug}`) ||
    !!sessionStorage.getItem(`userTkn-${slug}`);

  const navigate = useNavigate();
  const { setHasClinics } = useDoctorContext();

  const hasClinics = doctor?.clinics?.length > 0;

  const url = window.location.href;
  const currentPath = url.substring(url.lastIndexOf("/") + 1);
  const isServicesPage = currentPath === "clinics";

  const clinicsToShow =
    isServicesPage || showAll
      ? doctor?.clinics || []
      : doctor?.clinics?.slice(0, 4) || [];

  const hasMoreClinics = !isServicesPage && doctor?.clinics?.length > 4;

  if (!hasClinics) {
    setHasClinics(false);
    return null;
  } else {
    setHasClinics(true);
  }

  const handleProtectedNavigation = (route) => {
    if (!isAuthorized) {
      setPendingRoute(route);
      setIsModalVisible(true);
    } else {
      navigate(route);
    }
  };

  const handleGuestBooking = () => {
    setIsModalVisible(false);
    setIsGuestModalVisible(true);
  };

  const handleGuestInfoSubmit = (guestInfo) => {
    setIsGuestModalVisible(false);
    sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
    sessionStorage.setItem(`isGuestBooking-${slug}`, "true");
    navigate(pendingRoute || `/book-appointment`);
  };

 
  const toggleClinicDetails = (clinicId) => {
    setExpandedClinic(expandedClinic === clinicId ? null : clinicId);
  };

 

  const handleCopyEmail = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    message.success("Copied to clipboard");
  };

  return (
    <section
      ref={sectionRef}
      className="min-h-screen max-w-8xl  mx-auto flex flex-col justify-center items-center sm:p-10 p-3 "
    >
      <div className="text-center mb-6 md:mb-14">
        <h2 className=" text-2xl md:text-4xl font-bold text-center goldy flex items-center justify-center gap-3">
          <BiClinic className="text-xl md:text-3xl text-primary_hover" />
          {t("clinics.title")}
        </h2>
        <p className="mt-3 text-gray-500 text-sm md:text-base max-w-2xl mx-auto">
          {t("clinics.subtitle")}
        </p>
      </div>

      {hasClinics ? (
        <>
          <motion.div
            className={`grid gap-6 md:grid-cols-3 w-full ${
              isServicesPage
                ? "lg:grid-cols-3 xl:grid-cols-3"
                : "lg:grid-cols-2 xl:px-40"
            }`}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.15,
                },
              },
            }}
          >
            {clinicsToShow.map((clinic) => (
              <motion.div
                key={clinic.clinicId}
                className="bg-cardColor border border-primary/30 rounded-2xl p-3 md:p-6 shadow-md flex flex-col justify-between"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 20px rgba(0,0,0,0.12)",
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div>
                  <h3 className="md:text-xl font-semibold text-gray-500 mb-4 flex items-center gap-2">
                    <BiClinic className="text-xl" /> {clinic.clinicName}
                  </h3>
                  {clinic.hasLocation ? (
                    <div className="space-y-4 text-sm text-textPrimary">
                      {/* Address */}
                      <div className="flex items-start gap-3">
                        <MdOutlineLocationOn className="md:text-lg mt-0.5 text-primary" />
                        <div>
                          <p className="text-sm md:text-base font-semibold mb-1">
                            {t("clinics.card.address")}
                          </p>
                          <p className="text-gray-700 line-clamp-2">
                            {clinic.detailedAddress}
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            handleCopyEmail(clinic.detailedAddress)
                          }
                          className=" mx-2 text-primary hover:text-secondary"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Phone */}
                      <div className="flex items-start gap-3">
                        <LuPhoneCall className="md:text-lg mt-0.5 text-primary" />
                        <div>
                          <p className="text-sm md:text-base font-semibold mb-1">
                            {t("clinics.card.phone")}
                          </p>
                          <p className="text-gray-700">
                            {clinic.contactPhone || t("common.notAvailable")}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyEmail(clinic.contactPhone)}
                          className="ml-2 text-primary hover:text-secondary"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Email */}
                      <div className="flex items-start gap-3">
                        <MdOutlineEmail className="md:text-lg mt-0.5 text-primary" />
                        <div>
                          <p className="text-sm md:text-basefont-semibold mb-1">
                            {t("clinics.card.email")}
                          </p>
                          <p className="text-gray-700">
                            {clinic.contactEmail || t("common.notAvailable")}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyEmail(clinic.contactEmail)}
                          className="ml-2 text-primary hover:text-secondary"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 text-sm text-textPrimary">
                      <div className="flex items-start h-8 gap-3"></div>
                    </div>
                  )}
                </div>

                <div className="mt-2 flex gap-2 justify-end ">
                  <Button
                    className="w-[35%] md:w-[50%] text-white border text-sm md:text-lg justify-center items-center flex py-5 border-primary bg-goldy font-semibold  px-4 rounded-lg hover:bg-secondary/90 transition-colors shadow-md hover:shadow-lg"
                    type=""
                    onClick={() =>
                      handleProtectedNavigation(
                        `/book-appointment?ClinicId=${clinic.clinicId}&showOptions=false`,
                      )
                    }
                  >
                    {t("clinics.bookHere")}
                  </Button>
                  <button
                    className="w-[35%] md:w-[50%] bg-white text-primary border border-primary font-semibold py-1 px-4 rounded-lg hover:bg-cardColor flex items-center justify-center  transition-colors shadow-md  gap-2"
                    onClick={() => toggleClinicDetails(clinic.clinicId)}
                  >
                    <LuEye className="text-sm" />
                    {t("clinics.details.button")}
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* 👇 updated: See More / See Less toggle */}
          {hasMoreClinics && (
            <motion.div
              className="text-center mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <button
                onClick={() => {
                  if (showAll) {
                    sectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                  setShowAll((prev) => !prev);
                }}
className="bg-white border-2 border-primary font-semibold py-2 sm:py-3 px-4 sm:px-8 rounded-lg hover:bg-cardColor hover:scale-105 text-primary text-xs sm:text-sm transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-1.5 sm:gap-2 mx-auto"              >
                <BiClinic className="text-lg" />
                {showAll
                  ? t("clinics.seeLess")
                  : t("clinics.seeAll", { count: doctor.clinics.length })}
              </button>
            </motion.div>
          )}
        </>
      ) : (
        <div className="text-center py-16 text-gray-600">
          <HiOutlineBuildingOffice2 className="text-6xl mx-auto text-primary mb-4" />
          <h3 className="text-xl font-semibold mb-2">
            {t("clinics.noClinics.title")}
          </h3>
          <p className="mb-4">{t("clinics.noClinics.description")}</p>
          <button className="mt-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg">
            {t("clinics.noClinics.contactButton")}
          </button>
        </div>
      )}

      {/* Clinic Details Modal */}
      {expandedClinic && (
        <ClinicDetailsModal
          clinic={doctor.clinics.find((c) => c.clinicId === expandedClinic)}
          isOpen={!!expandedClinic}
          onClose={() => setExpandedClinic(null)}
          t={t}
          handleProtectedNavigation={handleProtectedNavigation}

        />
      )}

      <NeedAuthModal
        open={isModalVisible}
        onConfirm={() => {
          setIsModalVisible(false);
          setLoginModal(true);
        }}
        onCancel={() => {
          setIsModalVisible(false);
          setPendingRoute(null);
        }}
        onGuestBooking={handleGuestBooking}
      />
      <LoginModal
        setRegisterModal={setRegisterModal}
        isRegister={registerModal}
        loginModal={loginModal}
        setLoginModal={setLoginModal}
      />

      <GuestBookingModal
        open={isGuestModalVisible}
        onCancel={() => {
          setIsGuestModalVisible(false);
          setPendingRoute(null);
        }}
        onSubmit={handleGuestInfoSubmit}
      />
    </section>
  );
};

export default ClinicsSection;
